import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import _ from 'lodash';
import { CircularProgress, Switch, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useStyles } from '../../styles/styles';
import StyledCard from '../../components/cards/StyledCard';
import StyledCardContent from '../../components/cards/StyledCardContent';
import StyledCardHeader, { HeaderButtonIcon } from '../../components/cards/StyledCardHeader';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes, COUNTRIES } from '../../utils/constants';
import { NameValueRowList } from '../../components/cards/CardRows/NameValueRowList';
import { useProvider } from '../../state/providerStore';
import { Api } from '../../api';
import { greenColor } from '../../components/main/react/App';
import { TitleSubtitleRow } from '../../components/cards/CardRows/TitleSubtitleRow';
import ActionsList from '../../components/other/ActionsList';

const detailsRows = [
  { name: 'name' },
  { name: 'partnerId', label: 'Partner ID' },
  { name: 'isActive' },
  { name: 'id', label: 'Identifier' },
];

const addressRows = ['address', 'address2', 'zip', 'city', 'state', 'country', 'vat'];

const COMMUNICATION_MODULES = ['ViziDrive', 'OICP_Hubject', 'OCPI'];

const AVAILABLE_ROLES = ['CPO', 'EMSP', 'PAYMENT_PROCESSOR', 'SCSP', 'NAP', 'NSP', 'OTHER'];
const roleLabel = (role: string) => role.replaceAll('_', ' ');

const detailsEditInputs = [
  { name: 'name', type: FormInputTypes.textField },
  { name: 'partnerId', label: 'Partner ID', type: FormInputTypes.textField },
  { name: 'isActive', type: FormInputTypes.switch },
];

const addressEditInputs = [
  { name: 'address', type: FormInputTypes.textField },
  { name: 'address2', label: 'Address 2', type: FormInputTypes.textField },
  { name: 'zip', type: FormInputTypes.textField },
  { name: 'city', type: FormInputTypes.textField },
  { name: 'state', type: FormInputTypes.textField },
  { name: 'country', type: FormInputTypes.select, options: COUNTRIES },
  { name: 'vat', label: 'VAT', type: FormInputTypes.textField },
];

const buildCommunicationEditInputs = (onGetVersion: () => Promise<void>) => [
  {
    name: 'communicationModule',
    label: 'Module',
    type: FormInputTypes.select,
    options: COMMUNICATION_MODULES,
  },
  { name: 'versionsUrl', label: 'Versions URL', type: FormInputTypes.textField },
  { name: 'partnerToken', label: 'Initialization Token', type: FormInputTypes.textField },
  { name: 'communicationVersion', label: 'Version', type: FormInputTypes.disabledTextField },
  {
    name: 'getVersion',
    label: 'GET VERSION',
    type: FormInputTypes.textButton,
    alignment: 'flex-end',
    onClick: onGetVersion,
  },
];

const rolesEditInputs = AVAILABLE_ROLES.map((role) => ({
  name: `role_${role}`,
  label: roleLabel(role),
  type: FormInputTypes.switch,
}));

const contactAddInputs = [
  { name: 'firstName', label: 'First name', type: FormInputTypes.textField },
  { name: 'lastName', label: 'Last name', type: FormInputTypes.textField },
  { name: 'phone', label: 'Phone', type: FormInputTypes.textField },
  { name: 'email', label: 'email', type: FormInputTypes.textField },
  { name: 'position', label: 'Position', type: FormInputTypes.textField },
];

const contactEditInputs = [
  { name: 'id', label: 'Id', type: FormInputTypes.disabledTextField },
  { name: 'firstName', label: 'First name', type: FormInputTypes.textField },
  { name: 'lastName', label: 'Last name', type: FormInputTypes.textField },
  { name: 'phone', label: 'Phone', type: FormInputTypes.textField },
  { name: 'email', label: 'email', type: FormInputTypes.textField },
  { name: 'position', label: 'Position', type: FormInputTypes.textField },
];

const contractAddInputs = [
  { name: 'contractNumber', label: 'Contract number', type: FormInputTypes.textField },
  { name: 'startDate', label: 'Contract start date', type: FormInputTypes.textField },
  { name: 'endDate', label: 'Contract end date', type: FormInputTypes.textField },
];

const contractEditInputs = [
  { name: 'id', label: 'Id', type: FormInputTypes.disabledTextField },
  { name: 'contractNumber', label: 'Contract number', type: FormInputTypes.textField },
  { name: 'startDate', label: 'Contract start date', type: FormInputTypes.textField },
  { name: 'endDate', label: 'Contract end date', type: FormInputTypes.textField },
];

const ENDPOINT_TYPES = [
  'credentials',
  'locations',
  'sessions',
  'cdrs',
  'tariffs',
  'tokens',
  'commands',
  'CPO',
  'eMSP',
  'SCSP',
  'NAP',
  'NSP',
  'other',
];

const ENDPOINT_ROLES = ['SENDER', 'RECEIVER'];

const endpointAddInputs = [
  {
    name: 'type',
    label: 'Type',
    type: FormInputTypes.select,
    options: ENDPOINT_TYPES,
  },
  {
    name: 'role',
    label: 'Role',
    type: FormInputTypes.select,
    options: ENDPOINT_ROLES,
  },
  { name: 'url', label: 'URL', type: FormInputTypes.textField },
];

const endpointEditInputs = [
  {
    name: 'type',
    label: 'Type',
    type: FormInputTypes.select,
    options: ENDPOINT_TYPES,
  },
  {
    name: 'role',
    label: 'Role',
    type: FormInputTypes.select,
    options: ENDPOINT_ROLES,
  },
  { name: 'url', label: 'URL', type: FormInputTypes.textField },
];

const PartnerDetailsOverview = observer(({ partnerid }: { partnerid: string }) => {
  const classes = useStyles();
  const partnerProvider = useProvider(undefined, undefined, false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    partnerProvider.setRequest(() => Api.charge.getPartner(partnerid));
    partnerProvider.reloadResource();
  }, [partnerid]);

  const confirmEditDetails = () => {
    const values = toJS(drawerStore.values);
    Api.charge.updatePartner(partnerid, values).then(partnerProvider.reloadResource);
  };

  const onClickEditDetails = () => {
    drawerStore.openDrawer({
      label: 'Edit Details',
      inputs: detailsEditInputs,
      clickCallback: confirmEditDetails,
    });
    drawerStore.setValues(partnerProvider.data);
  };

  const confirmEditAddress = () => {
    const values = _.pick(toJS(drawerStore.values), [
      'address',
      'address2',
      'zip',
      'city',
      'state',
      'country',
      'vat',
    ]);
    Api.charge.updatePartner(partnerid, values).then(partnerProvider.reloadResource);
  };

  const onClickEditAddress = () => {
    drawerStore.openDrawer({
      label: 'Edit Address',
      inputs: addressEditInputs,
      clickCallback: confirmEditAddress,
    });
    drawerStore.setValues(partnerProvider.data);
  };

  const confirmEditCommunication = () => {
    const values = _.pick(toJS(drawerStore.values), [
      'communicationModule',
      'communicationVersion',
      'versionsUrl',
      'partnerToken',
    ]);
    Api.charge.updatePartner(partnerid, values).then(partnerProvider.reloadResource);
  };

  const onGetVersion = async () => {
    const values = toJS(drawerStore.values) || {};
    if (!values.versionsUrl || !values.partnerToken) {
      alert('Enter Versions URL and Initialization Token first.');
      return;
    }
    try {
      // Persist url + token so the backend has what fetchVersion needs to read.
      await Api.charge.updatePartner(partnerid, {
        versionsUrl: values.versionsUrl,
        partnerToken: values.partnerToken,
      });
      const response = await Api.charge.fetchPartnerVersion(partnerid);
      const updated = response?.data?.data || response?.data;
      const version = updated?.communicationVersion;
      if (version) {
        drawerStore.setValues({ ...drawerStore.values, communicationVersion: version });
      }
      partnerProvider.reloadResource();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to fetch version from partner.';
      alert(`GET VERSION failed: ${message}`);
    }
  };

  const onClickEditCommunication = () => {
    drawerStore.openDrawer({
      label: 'Edit Communication',
      inputs: buildCommunicationEditInputs(onGetVersion),
      clickCallback: confirmEditCommunication,
    });
    drawerStore.setValues(partnerProvider.data);
  };

  const confirmEditRoles = () => {
    const values = toJS(drawerStore.values);
    const roles = AVAILABLE_ROLES.filter((role) => values[`role_${role}`]);
    Api.charge.updatePartner(partnerid, { roles }).then(partnerProvider.reloadResource);
  };

  const onClickEditRoles = () => {
    drawerStore.openDrawer({
      label: 'Edit Roles',
      inputs: rolesEditInputs,
      clickCallback: confirmEditRoles,
    });
    const currentRoles: string[] = partnerProvider.data?.roles || [];
    const roleValues: Record<string, boolean> = {};
    AVAILABLE_ROLES.forEach((role) => {
      roleValues[`role_${role}`] = currentRoles.includes(role);
    });
    drawerStore.setValues(roleValues);
  };

  const systemCommandInputs = [
    {
      id: 'selectedCommand',
      label: 'Apply Option',
      name: 'selectedCommand',
      type: FormInputTypes.radioButton,
      options: [
        {
          label: 'REGENERATE SYSTEM TOKENS',
          value: 'regenerateTokens',
          description: 'Regenerates system tokens for authentication with remote system.',
        },
      ],
    },
  ];

  const onClickSystemCommands = () => {
    drawerStore.openDrawer({
      label: 'System command',
      inputs: systemCommandInputs,
      clickCallback: () => {
        const values = toJS(drawerStore.values);
        if (values?.selectedCommand === 'regenerateTokens') {
          Api.charge.generatePartnerToken(partnerid).then(partnerProvider.reloadResource);
        }
      },
      buttonLabel: 'Send',
    });
    drawerStore.setValues({ selectedCommand: 'regenerateTokens' });
  };

  const sendCommandInputs = [
    {
      id: 'selectedCommand',
      label: 'Apply Option',
      name: 'selectedCommand',
      type: FormInputTypes.radioButton,
      options: [
        {
          label: 'UPDATE CREDENTIALS',
          value: 'updateCredentials',
          description: 'Sends credentials token and updates partner credentials token if changed.',
        },
        {
          label: 'UPDATE LOCATIONS',
          value: 'updateLocations',
          description:
            'Requests locations from CPO partner and sends our locations to eMSP partner.',
        },
        {
          label: 'UPDATE TARIFFS',
          value: 'updateTariffs',
          description:
            'Requests price lists from CPO partner and sends our price lists to eMSP partner.',
        },
        {
          label: 'PUSH LOCATIONS',
          value: 'pushLocations',
          description: 'Pushes our assigned locations and charge points to the partner.',
        },
        {
          label: 'PUSH TARIFFS',
          value: 'pushTariffs',
          description: 'Pushes our tariffs/price lists for assigned charge points to the partner.',
        },
      ],
    },
  ];

  const onClickSendCommand = () => {
    drawerStore.openDrawer({
      label: 'Send command',
      inputs: sendCommandInputs,
      clickCallback: () => {
        const values = toJS(drawerStore.values);
        if (values?.selectedCommand) {
          Api.charge
            .sendPartnerCommand(partnerid, values.selectedCommand)
            .then(partnerProvider.reloadResource);
        }
      },
      buttonLabel: 'Send',
    });
  };

  // Contacts
  const addContact = () => {
    drawerStore.openDrawer({
      label: 'Create contact',
      inputs: contactAddInputs,
      clickCallback: () => {
        const newContact = toJS(drawerStore.values);
        const currentContacts = partnerProvider.data?.contacts || [];
        Api.charge
          .updatePartner(partnerid, { contacts: [...currentContacts, newContact] })
          .then(partnerProvider.reloadResource);
      },
      buttonLabel: 'Assign contact',
    });
  };

  const removeContact = (index: number) => {
    const currentContacts = [...(partnerProvider.data?.contacts || [])];
    currentContacts.splice(index, 1);
    Api.charge
      .updatePartner(partnerid, { contacts: currentContacts })
      .then(partnerProvider.reloadResource);
  };

  const editContact = (index: number) => {
    const contact = partnerProvider.data?.contacts?.[index];
    if (!contact) return;
    drawerStore.openDrawer({
      label: 'Update contact',
      inputs: contactEditInputs,
      clickCallback: () => {
        const updatedContact = toJS(drawerStore.values);
        const currentContacts = [...(partnerProvider.data?.contacts || [])];
        currentContacts[index] = updatedContact;
        Api.charge
          .updatePartner(partnerid, { contacts: currentContacts })
          .then(partnerProvider.reloadResource);
      },
      buttonLabel: 'UPDATE CONTACT',
    });
    drawerStore.setValues(contact);
  };

  // Contracts
  const addContract = () => {
    drawerStore.openDrawer({
      label: 'Create contract',
      inputs: contractAddInputs,
      clickCallback: () => {
        const newContract = toJS(drawerStore.values);
        const currentContracts = partnerProvider.data?.contracts || [];
        Api.charge
          .updatePartner(partnerid, { contracts: [...currentContracts, newContract] })
          .then(partnerProvider.reloadResource);
      },
      buttonLabel: 'Assign contract',
    });
  };

  const removeContract = (index: number) => {
    const currentContracts = [...(partnerProvider.data?.contracts || [])];
    currentContracts.splice(index, 1);
    Api.charge
      .updatePartner(partnerid, { contracts: currentContracts })
      .then(partnerProvider.reloadResource);
  };

  const editContract = (index: number) => {
    const contract = partnerProvider.data?.contracts?.[index];
    if (!contract) return;
    drawerStore.openDrawer({
      label: 'Update contract',
      inputs: contractEditInputs,
      clickCallback: () => {
        const updatedContract = toJS(drawerStore.values);
        const currentContracts = [...(partnerProvider.data?.contracts || [])];
        currentContracts[index] = updatedContract;
        Api.charge
          .updatePartner(partnerid, { contracts: currentContracts })
          .then(partnerProvider.reloadResource);
      },
      buttonLabel: 'UPDATE CONTRACT',
    });
    drawerStore.setValues(contract);
  };

  // Endpoints
  const addEndpoint = () => {
    drawerStore.openDrawer({
      label: 'Create endpoint',
      inputs: endpointAddInputs,
      clickCallback: () => {
        const newEndpoint = toJS(drawerStore.values);
        const currentEndpoints = partnerProvider.data?.endpoints || [];
        Api.charge
          .updatePartner(partnerid, { endpoints: [...currentEndpoints, newEndpoint] })
          .then(partnerProvider.reloadResource);
      },
      buttonLabel: 'ASSIGN ENDPOINT',
    });
  };

  const removeEndpoint = (index: number) => {
    const currentEndpoints = [...(partnerProvider.data?.endpoints || [])];
    currentEndpoints.splice(index, 1);
    Api.charge
      .updatePartner(partnerid, { endpoints: currentEndpoints })
      .then(partnerProvider.reloadResource);
  };

  const editEndpoint = (index: number) => {
    const endpoint = partnerProvider.data?.endpoints?.[index];
    if (!endpoint) return;
    drawerStore.openDrawer({
      label: 'Update endpoint',
      inputs: endpointEditInputs,
      clickCallback: () => {
        const updatedEndpoint = toJS(drawerStore.values);
        const currentEndpoints = [...(partnerProvider.data?.endpoints || [])];
        currentEndpoints[index] = updatedEndpoint;
        Api.charge
          .updatePartner(partnerid, { endpoints: currentEndpoints })
          .then(partnerProvider.reloadResource);
      },
      buttonLabel: 'UPDATE ENDPOINT',
    });
    drawerStore.setValues(endpoint);
  };

  // Logos
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const { data } = await Api.docs.createImageForLocation(formData);
      const uploadedImage = data.data.medium || data.data.original || '';
      const currentLogos = partnerProvider.data?.logos || [];
      await Api.charge.updatePartner(partnerid, { logos: [...currentLogos, uploadedImage] });
      partnerProvider.reloadResource();
    } catch (err) {
      console.error('Failed to upload logo', err);
    }

    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const removeLogo = (index: number) => {
    const currentLogos = [...(partnerProvider.data?.logos || [])];
    currentLogos.splice(index, 1);
    Api.charge
      .updatePartner(partnerid, { logos: currentLogos })
      .then(partnerProvider.reloadResource);
  };

  if (partnerProvider.loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '60vh',
        }}
      >
        <CircularProgress style={{ color: greenColor }} />
      </div>
    );
  }

  if (partnerProvider.data !== undefined) {
    return (
      <div className={classes.cardRows} style={{ flexWrap: 'wrap' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            marginBottom: '0.5em',
          }}
        >
          <Button
            variant="text"
            size="small"
            style={{ color: greenColor, fontWeight: 600, textTransform: 'none' }}
            onClick={onClickSystemCommands}
          >
            SYSTEM COMMANDS
          </Button>
        </div>
        <div className={classes.leftCardRow}>
          {/* Details Card */}
          <StyledCard>
            <StyledCardHeader
              title="Details"
              onClick={onClickEditDetails}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <NameValueRowList
                rows={detailsRows}
                values={{
                  ...partnerProvider.data,
                  isActive:
                    (partnerProvider.data?.roles || []).length > 0 &&
                    partnerProvider.data?.isActive,
                }}
              />
            </StyledCardContent>
          </StyledCard>

          {/* Address Card */}
          <StyledCard>
            <StyledCardHeader title="Address" onClick={onClickEditAddress} />
            <StyledCardContent>
              <NameValueRowList rows={addressRows} values={partnerProvider.data} />
            </StyledCardContent>
          </StyledCard>

          {/* Contacts Card */}
          <StyledCard>
            <StyledCardHeader
              title="Contacts"
              onClick={addContact}
              headerButtonIcon={HeaderButtonIcon.add}
            />
            <StyledCardContent>
              {(partnerProvider.data?.contacts || []).map((contact: any, index: number) => (
                <TitleSubtitleRow
                  key={index}
                  title={[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
                  subtitle={[contact.position, contact.email, contact.phone]
                    .filter(Boolean)
                    .join(' | ')}
                  actions={
                    <ActionsList
                      actions={[
                        { label: 'Edit', callback: () => editContact(index) },
                        { label: 'Remove', callback: () => removeContact(index) },
                      ]}
                    />
                  }
                />
              ))}
            </StyledCardContent>
          </StyledCard>

          {/* Contracts Card */}
          <StyledCard>
            <StyledCardHeader
              title="Contracts"
              onClick={addContract}
              headerButtonIcon={HeaderButtonIcon.add}
            />
            <StyledCardContent>
              {(partnerProvider.data?.contracts || []).map((contract: any, index: number) => (
                <TitleSubtitleRow
                  key={index}
                  title={contract.contractNumber}
                  subtitle={[contract.startDate, contract.endDate].filter(Boolean).join(' - ')}
                  actions={
                    <ActionsList
                      actions={[
                        { label: 'Edit', callback: () => editContract(index) },
                        { label: 'Remove', callback: () => removeContract(index) },
                      ]}
                    />
                  }
                />
              ))}
            </StyledCardContent>
          </StyledCard>
        </div>

        <div className={classes.rightCardRow}>
          {/* Roles Card */}
          <StyledCard>
            <StyledCardHeader
              title="Roles"
              onClick={onClickEditRoles}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {AVAILABLE_ROLES.map((role) => {
                  const isActive = (partnerProvider.data?.roles || []).includes(role);
                  return (
                    <div
                      key={role}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem 0',
                      }}
                    >
                      <span style={{ minWidth: 60, textAlign: 'right' }}>{roleLabel(role)}</span>
                      <Switch
                        checked={isActive}
                        disabled
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: greenColor },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: greenColor,
                          },
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </StyledCardContent>
          </StyledCard>

          {/* Communication Type Card */}
          <StyledCard>
            <StyledCardHeader
              title="Communication Type"
              onClick={onClickEditCommunication}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '0.75rem',
                }}
              >
                <span style={{ color: '#888', minWidth: 120 }}>Module</span>
                <span style={{ flex: 1 }}>{partnerProvider.data?.communicationModule || '-'}</span>
                {partnerProvider.data?.communicationModule === 'OCPI' && (
                  <Button
                    variant="text"
                    size="small"
                    style={{ color: greenColor, fontWeight: 600, textTransform: 'none' }}
                    onClick={onClickSendCommand}
                  >
                    SEND COMMAND
                  </Button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.75rem' }}>
                <span style={{ color: '#888', minWidth: 120 }}>Version</span>
                <span>{partnerProvider.data?.communicationVersion || '-'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.75rem' }}>
                <span style={{ color: '#888', minWidth: 120 }}>Versions URL</span>
                <span style={{ wordBreak: 'break-all' }}>
                  {partnerProvider.data?.versionsUrl || '-'}
                </span>
              </div>
            </StyledCardContent>
          </StyledCard>

          {/* Endpoints Card */}
          <StyledCard>
            <StyledCardHeader
              title="URL Endpoints"
              onClick={addEndpoint}
              headerButtonIcon={HeaderButtonIcon.add}
            />
            <StyledCardContent>
              {(partnerProvider.data?.endpoints || []).map((endpoint: any, index: number) => (
                <TitleSubtitleRow
                  key={index}
                  title={endpoint.url}
                  subtitle={[endpoint.role, endpoint.type].filter(Boolean).join(' - ')}
                  actions={
                    <ActionsList
                      actions={[
                        { label: 'Edit', callback: () => editEndpoint(index) },
                        { label: 'Remove', callback: () => removeEndpoint(index) },
                      ]}
                    />
                  }
                />
              ))}
            </StyledCardContent>
          </StyledCard>

          {/* Logos Card */}
          <StyledCard>
            <StyledCardHeader
              title="Logos"
              onClick={() => logoInputRef.current?.click()}
              headerButtonIcon={HeaderButtonIcon.add}
            />
            <StyledCardContent>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoUpload}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(partnerProvider.data?.logos || []).map((src: string, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      position: 'relative',
                      width: 70,
                      height: 70,
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0',
                      background: '#fff',
                    }}
                  >
                    <img
                      src={src}
                      alt="Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div
                      onClick={() => removeLogo(idx)}
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 20,
                        height: 20,
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        border: '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 2,
                      }}
                    >
                      <CloseIcon style={{ fontSize: 12 }} />
                    </div>
                  </div>
                ))}
              </div>
            </StyledCardContent>
          </StyledCard>
        </div>
      </div>
    );
  } else {
    return <div />;
  }
});

export default PartnerDetailsOverview;
